import { DateTimePicker, Feedback, SelectInput, TextInput } from 'canoe';
import { FieldMetaProps, useFormikContext } from 'formik';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import moment from 'moment/moment';
import React, { useMemo } from 'react';

import { TraitTypes } from '@candu-ai/segments/types';
import { isISOString } from 'dashboard/pages/Segment/components/DefinitionForm/DefintionMapping';
import {
  getNameDifferentiator,
  stripNameDifferentiator,
} from 'dashboard/pages/Segment/components/DefinitionForm/nameDifferentiatorUtils';

import { OperationOperandAutocompleteField } from './OperationOperandAutocomplete';
import { FieldWithErrors } from './StyledForm';
import { FilterOperations, UserPropertyFilter } from './types';

export const UserPropertyOperandField = ({
  getFormattedResults,
  onChange,
  meta,
  ...props
}: {
  getFormattedResults: (results: any) => any[];
  meta: FieldMetaProps<string>;
} & Record<any, any>) => {
  const {
    values: {
      traitName,
      operation: { type: operationType, operand },
    },
    setFieldValue,
  } = useFormikContext<UserPropertyFilter>();

  const traitType = useMemo(() => getNameDifferentiator(traitName), [traitName]);

  // Boolean types should always only show two options
  if (traitType === TraitTypes.Boolean) {
    const booleanOptions = [
      {
        label: 'true',
        value: 'true',
      },
      {
        label: 'false',
        value: 'false',
      },
    ];

    let currentValue = operand;

    // if the value is empty or not a valid option, set as first option
    if (operand === '' || !booleanOptions.find((option) => option.value === operand)) {
      currentValue = booleanOptions[0].value;
      setFieldValue('operation.operand', currentValue, false);
    }

    return (
      <FieldWithErrors>
        <SelectInput
          {...props}
          items={booleanOptions}
          selectedItem={booleanOptions.find((option) => option.value === currentValue)}
          onSelect={(selected) => onChange(selected?.value)}
          state={meta.touched && meta.error ? 'danger' : undefined}
        />
        {meta.touched && meta.error && (
          <Feedback mt="xxs" color="danger">
            {meta.error}
          </Feedback>
        )}
      </FieldWithErrors>
    );
  }

  // Date types should always show a date picker
  if (traitType === TraitTypes.Date) {
    let currentValue;

    // if it's an empty value, a number (e.g. switching from relative date) or it's not a valid date
    // datepicker crashes if value is not Date | undefined
    if (
      operand === '' ||
      isBoolean(operand) ||
      isNumber(operand) ||
      !isISOString(operand) ||
      !moment(operand).isValid()
    ) {
      currentValue = undefined;
      if (operand !== '') {
        setFieldValue('operation.operand', '', false);
      }
    } else {
      currentValue = new Date(operand);
    }

    return (
      <DateTimePicker
        {...props}
        mode="date"
        selectedDate={currentValue}
        onConfirm={(date) => onChange(date ? date.toISOString() : date)}
        formatDate={(date) => {
          if (!date) {
            return '';
          }

          const month = date.getMonth() + 1;
          const day = date.getDate();

          return `${month < 10 ? '0' : ''}${month}/${
            day < 10 ? '0' : ''
          }${day}/${date.getFullYear()}`;
        }}
        state={meta.touched && meta.error ? 'danger' : undefined}
      />
    );
  }

  const isAutocompleteField =
    operationType === FilterOperations.Equals || operationType === FilterOperations.EqualsNot;
  const isMultiSelect =
    operationType === FilterOperations.OneOf || operationType === FilterOperations.NotOneOf;

  if (isAutocompleteField || isMultiSelect) {
    const apiQuery = (search: string) => ({
      query: {
        finder: 'byUserTraitValue',
        field: stripNameDifferentiator(traitName),
        q: `${search.trim()}`,
      },
    });

    return (
      <OperationOperandAutocompleteField
        apiQuery={apiQuery}
        getFormattedResults={getFormattedResults}
        onChange={onChange}
        isMultiselect={isMultiSelect}
        {...props}
      />
    );
  }

  // Number types should always show numeric input
  if (traitType === TraitTypes.Number) {
    let currentValue = operand;

    // if the input type is numeric and it contains a non-numeric value, set as empty
    if (operand !== '' && !isNumber(parseFloat(operand))) {
      currentValue = '';
      setFieldValue('operation.operand', '', false);
    }

    return (
      <FieldWithErrors>
        <TextInput
          type="number"
          {...props}
          value={currentValue ? parseFloat(currentValue) : currentValue}
          onChange={(e: any) => onChange(e.currentTarget.value)}
          state={meta.touched && meta.error ? 'danger' : undefined}
        />
        {meta.touched && meta.error && (
          <Feedback mt="xxs" color="danger">
            {meta.error}
          </Feedback>
        )}
      </FieldWithErrors>
    );
  }

  return (
    <FieldWithErrors>
      <TextInput
        {...props}
        type="text"
        onChange={(e: any) => onChange(e.currentTarget.value)}
        state={meta.touched && meta.error ? 'danger' : undefined}
      />
      {meta.touched && meta.error && (
        <Feedback mt="xxs" color="danger">
          {meta.error}
        </Feedback>
      )}
    </FieldWithErrors>
  );
};
